// API client with automatic static-JSON fallback for offline / outage mode.
// If the live API at API_BASE fails or times out, we fall back to /blogs-fallback.json
// so the site never appears broken.

// IMPORTANT: must be HTTPS for production — browsers refuse to follow
// http->https redirects on CORS preflight requests.
const API_BASE =
  process.env.REACT_APP_API_BASE || "https://rashiddev.in/api";

const REQUEST_TIMEOUT_MS = 6000;

const FALLBACK_URL = "/blogs-fallback.json";
const RESUME_FALLBACK_URL = "/resumeData.json";

let fallbackCache = null;
let resumeFallbackCache = null;

async function loadFallback() {
  if (fallbackCache) return fallbackCache;
  try {
    const res = await fetch(FALLBACK_URL);
    if (!res.ok) throw new Error("Fallback fetch failed");
    fallbackCache = await res.json();
    return fallbackCache;
  } catch (e) {
    console.warn("Fallback JSON unavailable", e);
    return {blogs: [], users: [], comments: []};
  }
}

function withTimeout(promise, ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("Request timed out")), ms);
    // Honor an externally-supplied abort signal so the caller's race wins
    // over the timeout race — we want a proper AbortError, not "timed out".
    const onAbort = () => {
      clearTimeout(t);
      const err = new Error("Aborted");
      err.name = "AbortError";
      reject(err);
    };
    if (signal) {
      if (signal.aborted) return onAbort();
      signal.addEventListener("abort", onAbort, {once: true});
    }
    promise.then(
      v => { clearTimeout(t); resolve(v); },
      e => { clearTimeout(t); reject(e); }
    );
  });
}

async function request(path, options = {}, token = null) {
  const url = `${API_BASE}${path}`;
  const {signal, ...rest} = options;
  const headers = {
    "Content-Type": "application/json",
    ...(rest.headers || {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const fetchPromise = fetch(url, {...rest, headers, signal});
  const res = await withTimeout(fetchPromise, REQUEST_TIMEOUT_MS, signal);
  const text = await res.text().catch(() => "");
  // Try to parse JSON either way — our PHP returns JSON even on 500.
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch (_) { /* not JSON */ }

  if (!res.ok) {
    // Surface the real PHP/SQL message when available.
    const msg = body?.message || body?.error || text || res.statusText;
    const err = new Error(`API ${res.status}: ${msg}`);
    err.status = res.status;
    err.body = body;
    console.error("[API error]", url, body || text);
    throw err;
  }
  return body;
}

// ---------- Public API with graceful fallback ----------

// ---- Helpers used by the offline (static JSON) fallback so search/filter/sort
//      still work when the PHP backend is unreachable.
function applyStaticFilters(list, {q = "", sortBy = "created", dir = "desc", categories = []} = {}) {
  let out = [...(list || [])];

  // Category multi-select (IN-list semantics)
  if (categories && categories.length) {
    const set = new Set(categories.map(c => c.toLowerCase()));
    out = out.filter(b => set.has((b.category || "").toLowerCase()));
  }

  // CONTEXTUAL search — happens AFTER category filter so search stays inside
  // the selected categories.
  if (q) {
    const needle = q.toLowerCase();
    const titleHits = out.filter(b => (b.title || "").toLowerCase().includes(needle));
    out = titleHits.length
      ? titleHits
      : out.filter(b =>
          (b.title || "").toLowerCase().includes(needle) ||
          (b.content || "").toLowerCase().includes(needle)
        );
  }

  // Sort
  const cmpField = {
    created: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    likes:   (a, b) => (a.likes || 0) - (b.likes || 0),
    views:   (a, b) => (a.views || 0) - (b.views || 0),
  }[sortBy] || ((a, b) => new Date(a.created_at) - new Date(b.created_at));

  out.sort((a, b) => {
    const c = cmpField(a, b);
    return dir === "desc" ? -c : c;
  });
  return out;
}

export async function fetchBlogs({
  page = 1,
  perPage = 15,
  cursor = "",
  sortBy = "created",
  dir = "desc",
  q = "",
  categories = [],
  signal,
} = {}) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("per_page", String(perPage));
  if (cursor) qs.set("cursor", cursor);
  qs.set("sort_by", sortBy);
  qs.set("dir", dir);
  if (q) qs.set("q", q);
  // CRITICAL: PHP collapses repeated `?key=` to the LAST value unless the
  // key ends in `[]`. Use `category[]=A&category[]=B` so PHP populates
  // $_GET['category'] as an actual array.
  (categories || []).forEach(c => { if (c) qs.append("category[]", c); });

  try {
    return await request(`/blogs.php?${qs.toString()}`, {signal});
  } catch (e) {
    // If the caller aborted (route change, new search), don't fall back —
    // just propagate so the caller can ignore it.
    if (e.name === "AbortError" || signal?.aborted) throw e;
    // GOLDEN RULE: fallback to static JSON so the UI never goes blank.
    console.warn("[fallback] fetchBlogs", e.message);
    const fb = await loadFallback();
    const filtered = applyStaticFilters(fb.blogs, {q, sortBy, dir, categories});
    const start = (page - 1) * perPage;
    const slice = filtered.slice(start, start + perPage + 1);
    const hasMore = slice.length > perPage;
    const pageRows = hasMore ? slice.slice(0, perPage) : slice;
    return {
      blogs: pageRows,
      total: filtered.length,
      page,
      per_page: perPage,
      sort_by: sortBy,
      dir,
      q,
      categories,
      has_more: hasMore,
      next_cursor: null,
      offline: true,
    };
  }
}

// Distinct categories with post counts. Falls back to deriving them from
// the static JSON if /categories.php is unreachable.
export async function fetchCategories({signal} = {}) {
  try {
    return await request(`/categories.php`, {signal});
  } catch (e) {
    if (e.name === "AbortError" || signal?.aborted) throw e;
    console.warn("[fallback] fetchCategories", e.message);
    const fb = await loadFallback();
    const counts = new Map();
    (fb.blogs || []).forEach(b => {
      const c = (b.category || "").trim();
      if (!c) return;
      counts.set(c, (counts.get(c) || 0) + 1);
    });
    const categories = [...counts.entries()]
      .map(([name, count]) => ({name, count}))
      .sort((a, b) => a.name.localeCompare(b.name));
    return {categories, offline: true};
  }
}

export async function fetchBlog(id) {
  try {
    return await request(`/blog.php?id=${encodeURIComponent(id)}`);
  } catch (e) {
    console.warn("[fallback] fetchBlog", e.message);
    const fb = await loadFallback();
    const blog = (fb.blogs || []).find(b => String(b.id) === String(id));
    if (!blog) throw new Error("Blog not found (offline mode)");
    return {blog, comments: [], offline: true};
  }
}

// Persistent per-browser identifier used for guest view dedup.
function getClientToken() {
  let t = null;
  try { t = localStorage.getItem("rd_client_token"); } catch (_) {}
  if (!t) {
    t =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    try { localStorage.setItem("rd_client_token", t); } catch (_) {}
  }
  return t;
}

export async function incrementView(id, token) {
  try {
    return await request(
      `/view.php`,
      {
        method: "POST",
        body: JSON.stringify({blog_id: id, client_token: getClientToken()})
      },
      token
    );
  } catch (e) {
    return {ok: false, offline: true};
  }
}

export async function likeBlog(id, token) {
  try {
    return await request(
      `/like.php`,
      {method: "POST", body: JSON.stringify({blog_id: id})},
      token
    );
  } catch (e) {
    return {ok: false, offline: true};
  }
}

export async function postComment(blogId, text, token, parentId = null) {
  return request(
    `/comment.php`,
    {
      method: "POST",
      body: JSON.stringify({blog_id: blogId, comment_text: text, parent_id: parentId})
    },
    token
  );
}

// ---------- Site settings ----------
export async function fetchSettings() {
  try {
    return await request(`/settings.php`);
  } catch (e) {
    console.warn("[settings] using defaults — API unreachable", e.message);
    return {settings: null, offline: true};
  }
}

export async function adminGetSettings(token) {
  return request(`/admin.php?action=settings_get`, {}, token);
}

export async function adminUpdateSettings(settings, token) {
  return request(
    `/admin.php?action=settings_update`,
    {method: "POST", body: JSON.stringify({settings})},
    token
  );
}

// ---------- Resume ----------
async function loadResumeFallback() {
  if (resumeFallbackCache) return resumeFallbackCache;
  try {
    const res = await fetch(RESUME_FALLBACK_URL);
    if (!res.ok) throw new Error("Resume fallback fetch failed");
    resumeFallbackCache = await res.json();
    return resumeFallbackCache;
  } catch (e) {
    console.warn("[resume] static fallback unavailable", e?.message);
    return null;
  }
}

export async function fetchResume() {
  try {
    const res = await request(`/resume.php`);
    if (res?.resume && Object.keys(res.resume).length > 0) {
      return {
        resume: res.resume,
        pdfEnabled: res.pdfEnabled !== false,
        offline: false
      };
    }
    // API reachable but empty content → use static JSON.
    const fb = await loadResumeFallback();
    return {resume: fb, pdfEnabled: res?.pdfEnabled !== false, offline: false};
  } catch (e) {
    console.warn("[resume] API unreachable, using static JSON", e?.message);
    const fb = await loadResumeFallback();
    return {resume: fb, pdfEnabled: true, offline: true};
  }
}

// Fire-and-forget tracker. Never throws.
export async function trackResumeEvent(event) {
  try {
    let clientToken = null;
    try { clientToken = localStorage.getItem("rd_client_token"); } catch (_) {}
    await request(`/resume.php?action=track`, {
      method: "POST",
      body: JSON.stringify({event, client_token: clientToken})
    });
  } catch (_) { /* analytics is best-effort */ }
}

export async function adminGetResume(token) {
  return request(`/admin.php?action=resume_get`, {}, token);
}

export async function adminUpdateResume(resume, token) {
  return request(
    `/admin.php?action=resume_update`,
    {method: "POST", body: JSON.stringify({resume})},
    token
  );
}

export async function adminGetResumeAnalytics(token) {
  return request(`/admin.php?action=resume_analytics`, {}, token);
}

// ---------- Contact form ----------
// Public submission. Backend always returns 200 on a successful DB write,
// even if email delivery fails — see api/contact.php.
export async function submitContactMessage(payload) {
  return request(`/contact.php`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

// ---------- Admin: contact inbox ----------
export async function adminListMessages(token) {
  return request(`/admin.php?action=messages_list`, {}, token);
}

export async function adminMarkMessageRead(id, isRead, token) {
  return request(
    `/admin.php?action=message_mark_read&id=${id}`,
    {method: "POST", body: JSON.stringify({is_read: isRead})},
    token
  );
}

export async function adminDeleteMessage(id, token) {
  return request(
    `/admin.php?action=message_delete&id=${id}`,
    {method: "POST"},
    token
  );
}

export async function login(username, password) {
  return request(`/auth.php?action=login`, {
    method: "POST",
    body: JSON.stringify({username, password})
  });
}

export async function register(username, email, password) {
  return request(`/auth.php?action=register`, {
    method: "POST",
    body: JSON.stringify({username, email, password})
  });
}

// ---------- Admin endpoints (no fallback — admin requires live API) ----------

export async function adminListBlogs(token) {
  return request(`/admin.php?action=list`, {}, token);
}

export async function adminCreateBlog(payload, token) {
  return request(
    `/admin.php?action=create`,
    {method: "POST", body: JSON.stringify(payload)},
    token
  );
}

export async function adminUpdateBlog(id, payload, token) {
  return request(
    `/admin.php?action=update&id=${id}`,
    {method: "POST", body: JSON.stringify(payload)},
    token
  );
}

export async function adminDeleteBlog(id, token) {
  return request(
    `/admin.php?action=delete&id=${id}`,
    {method: "POST"},
    token
  );
}
